/* eslint-disable no-case-declarations */
import { Action, ActionCreator, Reducer } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { CustomerAttribute } from '@common/customerAttributes/types.js';
import { Generation } from '@api-consent/types/common.js';
import { Client4p } from '@src/classes/Client4p.js';
import { ClientComs } from '@src/classes/ClientComs.js';
import { FeatureType } from '@src/defs/features.js';
import type { RootState } from '@src/store/index.js';
import {
  ConsentGroup, ConsentSaveType, ConsentType, FeatureItemType, PageDetails,
} from '@src/types/common.js';
import { mapConsentFeaturesTo, getAllFeatures } from '@src/util/consents.js';
import { makeCopy } from '@src/util/helpers.js';

// action types
export const LOAD = 'consents/LOAD';
export const LOAD_VIEW = 'consents/LOAD_VIEW';
export const SAVE = 'consents/SAVE';
export const TOGGLE = 'consents/TOGGLE';
export const TOGGLE_MERGE_ACCEPTED = 'consents/MERGECHANGE';
export const ONECLICK = 'consents/ONECLICK';
export const SET_COLLAPSIBLE_OPEN = 'consents/SET_COLLAPSIBLE_OPEN';
export const SET_EDIT_ACTIVE_GROUP_IND = 'consents/SET_EDIT_ACTIVE_GROUP_IND';
export const CANCEL_EDIT_MODE = 'consents/CANCEL_EDIT_MODE';
export const CANCEL_PAGE = 'consents/CANCEL_PAGE';
export const CANCEL_MICROFRONTEND = 'action/CANCEL_MICROFRONTEND';
export const DENY = 'consents/DENY';
export const PROCESSING = 'consents/PROCESSING';
export const ERROR = 'consents/ERROR';
export const SET_CUSTOMER_ATTRIBUTES = 'consents/SET_CUSTOMER_ATTRIBUTES';
export const RESET_CUSTOMER_ATTRIBUTES_ORIGIN = 'consents/RESET_CUSTOMER_ATTRIBUTES_ORIGIN';
export const RESET_CONSENT_GROUPS = 'consents/RESET_CONSENT_GROUPS';
export const RESET_RENDERED_CONSENT_VERSION_IDS = 'consents/RESET_RENDERED_CONSENT_VERSION_IDS';
export const ADD_RENDERED_CONSENT_VERSION_ID = 'consents/ADD_RENDERED_CONSENT_VERSION_ID';
export const REMOVE_RENDERED_CONSENT_VERSION_ID = 'consents/REMOVE_RENDERED_CONSENT_VERSION_ID';

// ----------------------------------------------------------------------------------------------
// STATE
// ----------------------------------------------------------------------------------------------

export type ConsentsState = {
  collapsibleOpenGroupName?: string;
  consentGroups?: ConsentGroup[];
  consentGroupsOrigin?: ConsentGroup[];
  consentGroupsView?: ConsentGroup[];
  customerAttributes?: CustomerAttribute[];
  customerAttributesOrigin?: CustomerAttribute[];
  details?: PageDetails,
  editmodeActiveGroupInd: number;
  error: boolean;
  generation?: Generation;
  globalOfferId?: string;
  mergeAccepted: boolean,
  processing: boolean;
  /**
   * A list of rendered (text, hidden, checkbox, ...) consent version ids
   * These consents have been "seen" by the user and were rendered on the page in this session.
   */
  renderedConsentVersionIds: string[]; // Not allowed to use Set() here :(
};

//
// Inital store state configuration
//
const initial: ConsentsState = {
  collapsibleOpenGroupName: undefined,
  consentGroups: undefined,
  consentGroupsOrigin: undefined,
  consentGroupsView: undefined,
  customerAttributes: [],
  customerAttributesOrigin: [],
  details: undefined,
  editmodeActiveGroupInd: -1,
  error: false,
  globalOfferId: undefined,
  mergeAccepted: false,
  processing: false,
  renderedConsentVersionIds: [],
};

// ----------------------------------------------------------------------------------------------
// action creators
// ----------------------------------------------------------------------------------------------
export const setCollapsibleOpen: ActionCreator<Action> = (
  groupName: string,
) => ({
  type: SET_COLLAPSIBLE_OPEN,
  groupName,
});

export const setActiveGroupInd = (groupInd = -1) => ({
  type: SET_EDIT_ACTIVE_GROUP_IND,
  groupInd,
});

export const cancelEditMode = () => ({
  type: CANCEL_EDIT_MODE,
});

export const cancelPage = () => ({
  type: CANCEL_PAGE,
});

export const cancelMicrofrontend = () => ({
  type: CANCEL_MICROFRONTEND,
});

export const load: ActionCreator<ThunkAction<Promise<Action>, RootState, void, Action>> = (sessionKeyFromSessionStorage: string) => async (
  dispatch,
  getState,
  // @ts-expect-error quietly ignore the error here
): Promise<Action> => {
  try {
    dispatch({ type: PROCESSING });
    const { config, auth, consents } = getState();
    const {
      mode, lifecycle, apiUri4p, sessionkey,
    } = config;
    const { spId, registerId } = auth;
    const { globalOfferId } = consents;

    // Check if it's a known registerId and set consents from current session storage.
    const globalOfferIdTheSameAsFromSessionStorage = globalOfferId && globalOfferId === `${spId}${registerId}`;
    // Also do this if the sessionkey on the component is the same as the one in the store
    const sessionkeyTheSameAsFromSessionStorage = sessionkey && sessionKeyFromSessionStorage === sessionkey;

    if (sessionkeyTheSameAsFromSessionStorage || globalOfferIdTheSameAsFromSessionStorage) {
      dispatch({
        type: LOAD,
        consentGroups: consents.consentGroups,
        globalOfferId,
        details: consents.details,
        customerAttributes: consents.customerAttributes,
        generation: consents.generation,
        renderedConsentVersionIds: consents.renderedConsentVersionIds,
      });
      // @ts-expect-error quietly ignore the error here
      return;
    }

    const client = apiUri4p
      ? new Client4p(config, auth)
      : new ClientComs(config, auth);

    let offerId;

    // mode might now contain a comma separated list of modes which we need to ignore here
    // and just take care of the first in the list
    // eslint-disable-next-line sonarjs/slow-regex
    const [modePart1, modePart2] = mode!.replace(/,.*$/, '').split(':');

    if (modePart1 === 'multiedit') {
      const lifecycleView = `${lifecycle}View`;
      const { groups } = await client.getConsents(lifecycleView);

      dispatch({
        type: LOAD_VIEW,
        consentGroups: groups,
      });
    }

    const {
      groups, details, customerAttributes, generation,
    } = await client.getConsents(lifecycle);

    if (registerId) {
      offerId = `${spId}${registerId}`;
    }

    //
    // Save the data to the store
    //
    dispatch({
      type: LOAD,
      consentGroups: groups,
      globalOfferId: offerId,
      details,
      customerAttributes,
      generation,
    });
    dispatch({ type: RESET_RENDERED_CONSENT_VERSION_IDS });

    // if mode = multiedit:xxx then try to open edit mode for group
    if (modePart1 === 'multiedit' && typeof modePart2 !== 'undefined') {
      // eslint-disable-next-line no-restricted-globals
      if (isNaN((modePart2 as unknown) as number)) {
        // modePart2 is group name
        dispatch(
          setActiveGroupInd(groups.findIndex(({ name }) => name === modePart2)),
        );
      } else if (groups[+modePart2]) {
        // modePart2 is numeric
        dispatch(setActiveGroupInd(+modePart2));
      }
    }
  } catch (error) {
    dispatch({
      type: ERROR,
      error,
    }); // TypeError if network error, timeout, or CORS problems
    throw error;
  }
};

export const save: ActionCreator<ThunkAction<void, RootState, void, Action>> = () => async (
  dispatch,
  getState,
): Promise<void> => {
  try {
    dispatch({ type: PROCESSING });
    const { config, auth, consents } = getState();
    const { apiUri4p } = config;
    const { consentGroups = [], customerAttributes, renderedConsentVersionIds } = consents;

    const client = apiUri4p
      ? new Client4p(config, auth)
      : new ClientComs(config, auth);

    // Since we now support undefined lcId when used with "group"
    // make sure lcId is at least set now
    const { lcId, registerId } = client.getCustomerInfoFromConfigOrAuth();

    if (!lcId && !registerId) {
      throw new Error('lcId or registerId needs to be set before calling .save()');
    }

    if (renderedConsentVersionIds.length === 0) {
      return;
    }

    // save consents
    const consentData: ConsentSaveType[] = []; // collect output data...

    if (consentGroups.length > 0) {
      consentGroups.forEach(({ consents: consentsInGroup = [], required, name }) => {
        // Notice that for Gen2 "required" will ALWAYS be false (validation in live-config) so the actual granted value will always be used.
        consentsInGroup
          .filter((consent) => !consent.deprecated) // remove all deprecated consents
          .filter((consent) => renderedConsentVersionIds.includes(`${name.toLowerCase()}_${consent.id}`)) // Include only those consents that the use has "seen"
          .forEach(({ id, granted = false, ...features }) => {
            // Generate consents out of the consents features
            const featureData = mapConsentFeaturesTo(
              features as ConsentType,

              ({ id: fid, granted: fgranted = false }) => ({
                id: fid,
                granted: required ? true : fgranted,
              }),
            );

            // Add the actual consent
            consentData.push({
              id,
              granted: required ? true : granted,
              ...featureData,
            });
          });
      });
    }
    await client.saveConsents(consentData, customerAttributes);

    dispatch({ type: SAVE });
    dispatch({ type: RESET_CUSTOMER_ATTRIBUTES_ORIGIN });
  } catch (error) {
    dispatch({ type: ERROR, error }); // TypeError if network error, timeout, or CORS problems
    throw error;
  }
};

export const saveAndLoad: ActionCreator<ThunkAction<void, RootState, void, Action>> = () => async (
  dispatch,
): Promise<void> => {
  await dispatch(save()); // eslint-disable-line
  await dispatch(load());
  dispatch(setActiveGroupInd());
};

export const toggle: ActionCreator<ThunkAction<void, RootState, void, Action>> = (
  { id, fid, feature }: { id: string, fid?: string, feature?: string },
) => async (
  dispatch,
  getState,
) => { // eslint-disable-line @typescript-eslint/require-await
  const {
    consents: { consentGroups },
  } = getState();
  // find affected consent and features
  let consentIndex: number;
  const groupIndex = consentGroups!.findIndex(({ consents }) => {
    consentIndex = consents.findIndex(({ id: cid }) => cid === id);
    return consentIndex > -1;
  });
  const newConsent = makeCopy<ConsentType>(
    // @ts-expect-error: id is known to exist
    consentGroups![groupIndex].consents[consentIndex],
  );
  const fValues = feature ? newConsent[feature as FeatureType] : undefined;
  const fValue = fValues
    ? fValues.find(({ id: fid2 }) => fid2 === fid)
    : undefined;
  const fValuesAll = getAllFeatures(newConsent);
  // set values consistenty
  if (fValue) {
    // click on feature checkbox
    fValue.granted = !fValue.granted;
    if (fValue.granted) {
      newConsent.granted = true;
    } else {
      const anyChecked = fValuesAll.find(({ granted }) => granted);
      if (!anyChecked) {
        newConsent.granted = false;
      }
    }
  } else {
    // click on consent checkbox
    newConsent.granted = !newConsent.granted;
    fValuesAll.forEach((fV) => {
      // eslint-disable-next-line no-param-reassign
      fV.granted = !!newConsent.granted;
    });
  }
  // update affected part of state to re-render view
  const newGroupConsents = [...consentGroups![groupIndex].consents];
  // @ts-expect-error: id is known to exist
  newGroupConsents.splice(consentIndex, 1, newConsent);
  const newGroup = {
    ...consentGroups![groupIndex],
    consents: newGroupConsents,
  };
  const newConsentGroups = [...consentGroups!];
  newConsentGroups.splice(groupIndex, 1, newGroup);

  dispatch({
    type: TOGGLE,
    consentGroups: newConsentGroups,
  });
};

const changeAllGroupConsents = (group2Change: ConsentGroup, value: boolean) => group2Change.consents.map((consent) => {
  const featureData = mapConsentFeaturesTo<FeatureItemType>(consent, (f) => ({
    ...f,
    granted: value,
  } as FeatureItemType));
  return {
    ...consent,
    granted: value,
    ...featureData,
  };
});

export const oneclick: ActionCreator<ThunkAction<void, RootState, void, Action>> = (
  groupIndex: number,
  checked: boolean,
) => async (
  dispatch,
  getState,
// eslint-disable-next-line @typescript-eslint/require-await
) => {
  const {
    consents: { consentGroups },
  } = getState();
  const oneclickGroup = consentGroups![groupIndex];
  const newGroupConsents = changeAllGroupConsents(oneclickGroup, checked);

  // update affected part of state to re-render view
  const newGroup = {
    ...consentGroups![groupIndex],
    consents: newGroupConsents,
  };
  const newConsentGroups = [...consentGroups!];
  newConsentGroups.splice(groupIndex, 1, newGroup);

  dispatch({
    type: ONECLICK,
    consentGroups: newConsentGroups,
  });
};

export const deny: ActionCreator<ThunkAction<void, RootState, void, Action>> = (
  groupIndex: number,
  checked: boolean,
) => async (
  dispatch,
  getState,
) => { // eslint-disable-line @typescript-eslint/require-await
  const {
    consents: { consentGroups, consentGroupsOrigin },
  } = getState();
  // set all consents to not granted
  const denyGroup = consentGroups![groupIndex];
  const newGroupConsents = checked
    // set all consents of the group to false
    ? changeAllGroupConsents(denyGroup, false)
    // set the consents to the original consents again
    : consentGroupsOrigin![groupIndex].consents;

  // update affected part of state to re-render view
  const newGroup = {
    ...consentGroups![groupIndex],
    consents: newGroupConsents,
  };
  const newConsentGroups = [...consentGroups!];
  newConsentGroups.splice(groupIndex, 1, newGroup);

  dispatch({
    type: DENY,
    consentGroups: newConsentGroups,
  });
};

export const setCustomerAttribute: ActionCreator<ThunkAction<void, RootState, void, Action>> = (
  customerAttribute : CustomerAttribute,
) => async (
  dispatch,
  getState,
) => { // eslint-disable-line @typescript-eslint/require-await
  const { consents: { customerAttributes } } = getState();
  const cleanCustomerAttributesFromStore = customerAttributes!.filter(
    (storeCattr) => storeCattr.id !== customerAttribute.id,
  );
  dispatch({
    type: SET_CUSTOMER_ATTRIBUTES,
    customerAttributes: [...cleanCustomerAttributesFromStore, customerAttribute],
  });
};

//
// Set the "granted" values of a consent, if the group has a defined "grantedDefault" for generation 2+
//
export const setGrantedValueForGroups: ActionCreator<ThunkAction<void, RootState, void, Action>> = () => (
  dispatch,
  getState,
) => {
  const {
    consents: { consentGroups, generation },
  } = getState();

  //
  // If generation 2+ consent groups we need to prepare those consents by
  // setting granted to the grantedDefault live-config value
  //
  if (generation! >= Generation.UNLOCK) {
    let changed = false;
    consentGroups!.forEach((consentGroup) => {
      if (typeof consentGroup.grantedDefault !== 'undefined') {
        consentGroup.consents.forEach((consent) => {
          consent.granted = consentGroup.grantedDefault; // eslint-disable-line no-param-reassign
          changed = true;
        });
      }
    });

    if (changed) {
      dispatch({
        type: RESET_CONSENT_GROUPS,
        consentGroups,
      });
    }
  }
};

/**
 * Add an consent version id to the list of rendered consent version ids.
 * When saving the consents via the API only consent version ids that are added this way
 * will actually be send to the backend.
 *
 * This works for all generations.
 */
export const addRenderedConsentVersionId: ActionCreator<ThunkAction<void, RootState, void, Action>> = (
  renderedGroupName: string,
  renderedConsentVersionId: string,
) => (
  dispatch,
  getState,
) => {
  const {
    consents: { renderedConsentVersionIds },
  } = getState();

  const id = `${renderedGroupName.toLowerCase()}_${renderedConsentVersionId}`;
  if (!renderedConsentVersionIds.includes(id)) {
    dispatch({
      type: ADD_RENDERED_CONSENT_VERSION_ID,
      renderedConsentVersionId: id,
    });
  }
};

/**
 * Removes an consent version id from the list of rendered consent version ids.
 * Removes and version id independent from which group it comes from.
 *
 * This only works for generation 2+ consent groups.
 */
export const removeRenderedConsentVersionId: ActionCreator<ThunkAction<void, RootState, void, Action>> = (
  renderedConsentVersionId: string,
) => (
  dispatch,
  getState,
) => {
  const {
    consents: { generation, renderedConsentVersionIds },
  } = getState();

  if (generation! >= Generation.UNLOCK) {
    // We need to remove all rendered ids from the list because consent version ids may be used in mutliple groups which produces id duplicates
    const idsToRemove = renderedConsentVersionIds.filter((id) => id.endsWith(`_${renderedConsentVersionId}`));
    idsToRemove.forEach((id) => {
      dispatch({
        type: REMOVE_RENDERED_CONSENT_VERSION_ID,
        renderedConsentVersionId: id,
      });
    });
  }
};

// ----------------------------------------------------------------------------------------------
// ACTIONS
// ----------------------------------------------------------------------------------------------

// What action TYPEs can be used?
type ConsentsAction = Action<
| typeof ADD_RENDERED_CONSENT_VERSION_ID
| typeof CANCEL_EDIT_MODE
| typeof CANCEL_PAGE
| typeof DENY
| typeof ERROR
| typeof LOAD
| typeof LOAD_VIEW
| typeof ONECLICK
| typeof PROCESSING
| typeof REMOVE_RENDERED_CONSENT_VERSION_ID
| typeof RESET_CONSENT_GROUPS
| typeof RESET_CUSTOMER_ATTRIBUTES_ORIGIN
| typeof RESET_RENDERED_CONSENT_VERSION_IDS
| typeof SAVE
| typeof SET_COLLAPSIBLE_OPEN
| typeof SET_CUSTOMER_ATTRIBUTES
| typeof SET_EDIT_ACTIVE_GROUP_IND
| typeof TOGGLE
| typeof TOGGLE_MERGE_ACCEPTED
> &
ConsentsState & {
  customerAttributes?: CustomerAttribute[];
  customerAttributesOrigin?: CustomerAttribute[];
  generation?: Generation;
  groupInd?: number;
  groupName?: string;
  renderedConsentVersionId?: string;
};

//
// Execute a actions TYPE returning the new complete consents store state
// @ts-expect-error: no idea why this is needed
export const reducer: Reducer<ConsentsState, ConsentsAction> = (
  state = initial,
  action = {} as ConsentsAction,
) => {
  const {
    type,
    consentGroups,
    error,
    globalOfferId,
    groupInd,
    groupName,
    details,
    customerAttributes,
    generation,
    renderedConsentVersionId,
  } = action;

  const { editmodeActiveGroupInd, mergeAccepted } = state;
  switch (type) {
    case CANCEL_EDIT_MODE:
      // have to reset consent status from consentGroupsView
      const consentGroupsToBeSet = [...state.consentGroups as ConsentGroup[]];
      const consentsPersistCopy = makeCopy(
      // @ts-expect-error "For testing purposes"
        state.consentGroupsOrigin[editmodeActiveGroupInd].consents,
      );

      consentGroupsToBeSet[
        editmodeActiveGroupInd
      ].consents = consentsPersistCopy;

      return {
        ...state,
        editmodeActiveGroupInd: -1,
        consentGroups: consentGroupsToBeSet,
        customerAttributes: structuredClone(state.customerAttributesOrigin),
      };
    case CANCEL_PAGE:
      return {
        ...state,
        consentGroups: makeCopy(state.consentGroupsOrigin),
        customerAttributes: structuredClone(state.customerAttributesOrigin),
      };
    case SET_EDIT_ACTIVE_GROUP_IND:
      return { ...state, editmodeActiveGroupInd: groupInd };
    case SET_COLLAPSIBLE_OPEN:
      return { ...state, collapsibleOpenGroupName: groupName };
    case LOAD_VIEW:
      return { ...state, consentGroupsView: consentGroups };
    case LOAD:
      return {
        ...state,
        consentGroups,
        consentGroupsOrigin: makeCopy(consentGroups),
        processing: false,
        error: false,
        globalOfferId,
        details,
        customerAttributes,
        customerAttributesOrigin: structuredClone(customerAttributes),
        generation,
      };
    case SAVE:
      return { ...state, processing: false, error: false };
    case TOGGLE_MERGE_ACCEPTED:
      return { ...state, mergeAccepted: !mergeAccepted };
    case ONECLICK:
    case DENY:
    case TOGGLE:
      return { ...state, consentGroups };
    case PROCESSING:
      return { ...state, processing: true, error: false };
    case ERROR:
      return { ...state, processing: false, error };
    case SET_CUSTOMER_ATTRIBUTES:
      return { ...state, customerAttributes };
    case RESET_CUSTOMER_ATTRIBUTES_ORIGIN:
      return { ...state, customerAttributesOrigin: structuredClone(customerAttributes) };
    case RESET_CONSENT_GROUPS:
      return {
        ...state,
        consentGroups,
        consentGroupsOrigin: makeCopy(consentGroups),
      };
    case ADD_RENDERED_CONSENT_VERSION_ID:
      const renderedConsentVersionIds = new Set(state.renderedConsentVersionIds);
      renderedConsentVersionIds.add(renderedConsentVersionId as string);

      return {
        ...state,
        renderedConsentVersionIds: [...renderedConsentVersionIds],
      };
    case REMOVE_RENDERED_CONSENT_VERSION_ID:
      const newRenderedConsentVersionIds = new Set(state.renderedConsentVersionIds);
      newRenderedConsentVersionIds.delete(renderedConsentVersionId as string);

      return {
        ...state,
        renderedConsentVersionIds: [...newRenderedConsentVersionIds],
      };
    case RESET_RENDERED_CONSENT_VERSION_IDS:
      return {
        ...state,
        renderedConsentVersionIds: [],
      };
    default:
      return state;
  }
};
